import { Box, Button, CircularProgress, FormControl, InputLabel, MenuItem, Select, Typography } from "@mui/material";
import { useRef, useState } from "react";
import { importConverter } from "../app/importConverter.js";

export default function ImportConverter({
  disabled,
  onClear,
  onConverted,
  onErrors,
}) {
  const [importFormat, setImportFormat] = useState("geojson"); // "geojson" | "csv" | "dxf"
  const [isPickingFile, setIsPickingFile] = useState(false);
  const [isParsingFile, setIsParsingFile] = useState(false);
  const [isConverting, setIsConverting] = useState(false);
  const parsingStartedAtRef = useRef(0);
  const [parsedFileInfo, setParsedFileInfo] = useState(null); // { name, featureCount }

  const accept =
    importFormat === "csv"
      ? ".csv,text/csv"
      : importFormat === "dxf"
        ? ".dxf,.dwg"
        : ".json,.geojson,application/json";

  const handleFileChosen = async (e) => {
    onErrors?.([]);
    setIsPickingFile(false);
    setParsedFileInfo(null);

    const uploadedFiles = Array.from(e.target.files || []);
    const file = uploadedFiles[0];
    if (!file) return;

    // Allow selecting the same file twice in a row (otherwise onChange may not fire)
    e.target.value = "";

    setIsParsingFile(true);
    parsingStartedAtRef.current = Date.now();
    // Let React paint "Parsing…" before heavy sync work
    await new Promise((r) => setTimeout(r, 0));

    try {
      const lower = String(file.name || "").toLowerCase();
      const isDxfOrDwg = importFormat === "dxf" || lower.endsWith(".dxf") || lower.endsWith(".dwg");
      if (isDxfOrDwg) setIsConverting(true);

      const out = await importConverter({ file, importFormat });
      if (out.errors?.length) {
        onErrors?.(out.errors);
        onConverted?.(null);
        return;
      }

      const geojson = out.geojson;
      setParsedFileInfo({
        name: file.name,
        featureCount: Array.isArray(geojson?.features) ? geojson.features.length : 0,
      });

      onConverted?.({ ...out, importFormat }, file);
    } catch (error) {
      console.log(error);
      onErrors?.([
        importFormat === "csv"
          ? "Error! The file is not a valid CSV file."
          : importFormat === "dxf"
            ? "Error! The file could not be converted from DXF/DWG."
            : "Error! The file is not a valid GeoJSON file.",
      ]);
      onConverted?.(null);
    } finally {
      setIsConverting(false);
      const elapsed = Date.now() - (parsingStartedAtRef.current || 0);
      const minVisibleMs = 200;
      if (elapsed >= minVisibleMs) {
        setIsParsingFile(false);
      } else {
        setTimeout(() => setIsParsingFile(false), minVisibleMs - elapsed);
      }
    }
  };

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
        <FormControl size="small" sx={{ minWidth: 240, flex: "1 1 240px" }}>
          <InputLabel>Format</InputLabel>
          <Select
            value={importFormat}
            label="Format"
            disabled={disabled}
            onChange={(e) => {
              setImportFormat(e.target.value);
              setParsedFileInfo(null);
              onErrors?.([]);
              onConverted?.(null);
              onClear?.();
            }}
          >
            <MenuItem value="geojson">GeoJSON (JSON)</MenuItem>
            <MenuItem value="csv">CSV</MenuItem>
            <MenuItem value="dxf">DXF/DWG</MenuItem>
          </Select>
        </FormControl>

        <Button
          variant="contained"
          disabled={disabled || isParsingFile}
          component="label"
          sx={{ flex: "0 0 auto" }}
        >
          Select file
          <input
            type="file"
            hidden
            disabled={disabled || isParsingFile}
            accept={accept}
            onClick={() => {
              onErrors?.([]);
              setParsedFileInfo(null);
              setIsPickingFile(true);
            }}
            onChange={handleFileChosen}
          />
        </Button>
      </Box>

      {(isPickingFile || isParsingFile || isConverting) && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1.5 }}>
          <CircularProgress size={16} />
          <Typography variant="body2" color="rgba(255,255,255,0.85)">
            {isPickingFile
              ? "Waiting for file selection…"
              : isConverting
                ? "Converting DXF…"
                : "Parsing file…"}
          </Typography>
        </Box>
      )}

      {parsedFileInfo && (
        <Typography variant="body2" color="rgba(255,255,255,0.85)" mt={2}>
          Loaded <b>{parsedFileInfo.featureCount}</b> feature(s) from <b>{parsedFileInfo.name}</b>.
        </Typography>
      )}
    </Box>
  );
}

